/**
 * Google Calendar 模組 - 輔助函數
 */

import type { OrderItemData } from '../order';
import type { CalendarEventData, SyncCalendarParams } from './types';

export type CalendarTimeSource = 'confirmed' | 'requested' | 'default';

export interface CalendarResolvedTime {
  time: string;
  source: CalendarTimeSource;
  note: string;
}

export interface CalendarTimeContext {
  pickupDate: string;
  returnDate: string;
  pickupLocationName?: string;
  returnLocationName?: string;
  pickup: CalendarResolvedTime;
  return: CalendarResolvedTime;
}

/**
 * 產生日曆事件標題
 * 格式：[門市名] 客戶名 - 器材數量件 #訂單編號 🚗有租車
 */
export function generateEventTitle(
  customerName: string,
  totalItems: number,
  orderNumber: string,
  pickupLocationName?: string,
  hasVehicle?: boolean
): string {
  const locationTag = pickupLocationName ? `[${pickupLocationName}]` : '[取貨]';
  const vehicleTag = hasVehicle ? ' 🚗有租車' : '';
  return `${locationTag} ${customerName} - ${totalItems}件 #${orderNumber}${vehicleTag}`;
}

/**
 * 產生日曆事件描述
 * 包含：器材清單、租車資訊、調度來源、處理人員
 */
export function generateEventDescription(
  orderNumber: string,
  orderItems: OrderItemData[],
  allocationSource?: string,
  adminName?: string,
  hasVehicle?: boolean,
  vehiclePlateNumber?: string,
  timeContext?: CalendarTimeContext
): string {
  const lines: string[] = [];

  lines.push(`訂單編號：${orderNumber}`);
  lines.push('');

  if (timeContext) {
    lines.push('🕒 取還件時間：');
    lines.push(`取件：${timeContext.pickupDate} ${timeContext.pickup.time}（${timeContext.pickup.note}）`);
    lines.push(`還件：${timeContext.returnDate} ${timeContext.return.time}（${timeContext.return.note}）`);
    lines.push(`取件門市：${timeContext.pickupLocationName || '未指定'}`);
    lines.push(`還件門市：${timeContext.returnLocationName || '未指定'}`);
    lines.push('');
  }
  
  // 🚗 租車資訊（放在最前面醒目位置）
  if (hasVehicle) {
    lines.push('🚗 【有租車】');
    if (vehiclePlateNumber) {
      lines.push(`   車牌號碼：${vehiclePlateNumber}`);
    }
    lines.push('   ※ 油錢、過路費另計');
    lines.push('');
  }
  
  lines.push('📦 器材清單：');

  orderItems.forEach((item, index) => {
    const productName = item.product?.name_zh || '未知器材';
    lines.push(`${index + 1}. ${productName} × ${item.quantity}`);
  });

  if (allocationSource) {
    lines.push('');
    lines.push(`🔄 調度來源：${allocationSource}`);
  }

  if (adminName) {
    lines.push('');
    lines.push(`👤 處理人員：${adminName}`);
  }

  return lines.join('\n');
}

/**
 * 計算總器材數量
 */
export function calculateTotalItems(orderItems: OrderItemData[]): number {
  return orderItems.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * 格式化日期時間為 ISO 格式（台灣時區）
 * @param date 日期字串 (YYYY-MM-DD)
 * @param time 時間字串 (HH:mm)
 */
export function formatDateTimeISO(date: string, time: string): string {
  return `${date}T${time}:00+08:00`;
}

/**
 * 預設取貨時間
 */
export const DEFAULT_PICKUP_TIME = '10:00';

/**
 * 預設歸還時間
 */
export const DEFAULT_RETURN_TIME = '18:00';

function cleanTime(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(trimmed)
    ? trimmed.slice(0, 5)
    : null;
}

/**
 * Resolve Google Calendar pickup time using:
 * confirmed_pickup_time -> requested_pickup_time -> 10:00 default.
 */
export function resolvePickupCalendarTime(params: {
  confirmedPickupTime?: string | null;
  requestedPickupTime?: string | null;
}): CalendarResolvedTime {
  const confirmed = cleanTime(params.confirmedPickupTime);
  if (confirmed) {
    return { time: confirmed, source: 'confirmed', note: '門市確認時間' };
  }

  const requested = cleanTime(params.requestedPickupTime);
  if (requested) {
    return { time: requested, source: 'requested', note: '客戶希望時間，仍待門市確認' };
  }

  return { time: DEFAULT_PICKUP_TIME, source: 'default', note: '未指定時間，使用系統預設' };
}

/**
 * Resolve Google Calendar return time using:
 * confirmed_return_time -> requested_return_time -> 18:00 default.
 */
export function resolveReturnCalendarTime(params: {
  confirmedReturnTime?: string | null;
  requestedReturnTime?: string | null;
}): CalendarResolvedTime {
  const confirmed = cleanTime(params.confirmedReturnTime);
  if (confirmed) {
    return { time: confirmed, source: 'confirmed', note: '門市確認時間' };
  }

  const requested = cleanTime(params.requestedReturnTime);
  if (requested) {
    return { time: requested, source: 'requested', note: '客戶希望時間，仍待門市確認' };
  }

  return { time: DEFAULT_RETURN_TIME, source: 'default', note: '未指定時間，使用系統預設' };
}

/**
 * Build Google Calendar event data without touching Supabase or Google APIs.
 */
export function buildCalendarEventData(params: SyncCalendarParams): CalendarEventData {
  const totalItems = calculateTotalItems(params.orderItems);
  const pickupTime = resolvePickupCalendarTime({
    confirmedPickupTime: params.confirmedPickupTime,
    requestedPickupTime: params.requestedPickupTime,
  });
  const returnTime = resolveReturnCalendarTime({
    confirmedReturnTime: params.confirmedReturnTime,
    requestedReturnTime: params.requestedReturnTime,
  });

  return {
    title: generateEventTitle(
      params.customerName,
      totalItems,
      params.orderNumber,
      params.pickupLocationName,
      params.hasVehicle
    ),
    description: generateEventDescription(
      params.orderNumber,
      params.orderItems,
      params.allocationSource,
      params.adminName,
      params.hasVehicle,
      params.vehiclePlateNumber,
      {
        pickupDate: params.startDate,
        returnDate: params.endDate,
        pickupLocationName: params.pickupLocationName,
        returnLocationName: params.returnLocationName,
        pickup: pickupTime,
        return: returnTime,
      }
    ),
    start: formatDateTimeISO(params.startDate, pickupTime.time),
    end: formatDateTimeISO(params.endDate, returnTime.time),
  };
}

/**
 * 解析日曆錯誤訊息
 * 將技術性錯誤轉換為使用者友善的訊息
 */
export function parseCalendarError(error: any): string {
  const message = error?.message || String(error);

  if (message.includes('授權')) {
    return '⚠️ Google Calendar 尚未授權，請先在據點管理頁面完成授權設定';
  }

  if (message.includes('Calendar ID')) {
    return '⚠️ 此據點尚未設定 Google Calendar ID，請先在據點管理頁面設定';
  }

  if (message.includes('not found') || message.includes('404')) {
    return '⚠️ 找不到指定的日曆，請確認 Calendar ID 是否正確';
  }

  if (message.includes('permission') || message.includes('403')) {
    return '⚠️ 沒有權限存取此日曆，請確認日曆共用設定';
  }

  return `同步日曆失敗：${message}`;
}
